import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ApiService from "../../api/apiService";
import { convertTo12HourPlusDateFormat } from "../../util/helperFunctions";
import "../../styles/view-request-detail.css";
import ImagePreviewModal from "./reusable/image-preview-modal";
import showSwal from "../swal.jsx";
import { common } from "../../util/common.js";

const CCAR_LIST_URL = "/requests?p=view-ccar";

const resolveReturnUrl = (value) => {
  if (value == null) return "";

  const trimmed = String(value).trim();
  const normalized = trimmed.toLowerCase();

  if (!trimmed || normalized === "null" || normalized === "undefined") {
    return "";
  }

  return trimmed;
};

function CCARRequestDetail({ setApproversData, userDetails }) {
  const location = useLocation();

  const [ccarData, setCCARData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnUrl, setReturnUrl] = useState("");
  const [isUserApprover, setUserApprover] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [copiedReference, setCopiedReference] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [approvalAttachment, setApprovalAttachment] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const approvalFileInputRef = useRef(null);

  const handleApproveDenyRequest = (status) => {
    setApprovalStatus(status);
    setApprovalRemarks("");
    setApprovalAttachment(null);
    setShowApprovalModal(true);
  };

  const submitApproval = async () => {
    try {
      setIsApprovalSubmitting(true);

      console.log("========== APPROVAL DATA ==========");
      console.log("ccarData:", ccarData);
      console.log("form_id:", ccarData?.form_id);
      console.log("form_request_id:", ccarData?.form_request_id);
      console.log("approvalStatus:", approvalStatus);
      console.log("approvalRemarks:", approvalRemarks);
      console.log("approvalAttachment:", approvalAttachment);

      const formRequestId = ccarData?.form_request_id;

      if (!formRequestId) {
        console.error("FORM REQUEST ID IS MISSING:", ccarData);

        showSwal("Form request ID is missing.");
        return;
      }

      const formData = new FormData();

      formData.append("form_id", ccarData?.form_id || "");

      formData.append("request_id", formRequestId);

      formData.append("status", approvalStatus ? "true" : "false");

      formData.append("remarks", approvalRemarks.trim());

      if (approvalAttachment) {
        formData.append("attachment", approvalAttachment);
      }

      console.log("========== FORM DATA ==========");

      for (const [key, value] of formData.entries()) {
        console.log(key, value);
      }

      const apiResponse = await ApiService.forms.approveDenyRequest(formData);

      console.log("APPROVAL RESPONSE:", apiResponse);

      if (!apiResponse?.success) {
        showSwal(apiResponse?.message || common.errors.APPROVE_REQUEST_ERROR);
        return;
      }

      setShowApprovalModal(false);
      setApprovalRemarks("");
      setApprovalAttachment(null);
      setApprovalStatus(null);

      leaveRequestDetail();
    } catch (error) {
      console.error("APPROVAL SUBMIT ERROR:", error);

      showSwal(common.errors.APPROVE_REQUEST_ERROR);
    } finally {
      setIsApprovalSubmitting(false);
    }
  };

  useEffect(() => {
    const getCCARRequest = async () => {
      try {
        setLoading(true);

        const searchParams = new URLSearchParams(location.search);

        const requestType = searchParams.get("t");
        const requestLinkId = searchParams.get("r");
        const requestReturnUrl = searchParams.get("r_url");

        setReturnUrl(resolveReturnUrl(requestReturnUrl));

        if (!requestType || !requestLinkId) {
          setCCARData(null);
          return;
        }

        const response = await ApiService.forms.getFormRequestDetail(
          requestType,
          requestLinkId,
        );

        if (!response?.success || !response?.request) {
          setCCARData(null);
          return;
        }

        const request = {
          ...response.request,
          prospecting_images: response.request?.prospecting_images ?? "",
        };

        setCCARData(request);

        if (setApproversData) {
          setApproversData(request?.approval);
        }
      } catch (error) {
        console.error("Failed to retrieve CCAR request:", error);

        setCCARData(null);
      } finally {
        setLoading(false);
      }
    };

    getCCARRequest();
  }, [location.search, setApproversData]);

  useEffect(() => {
    if (!userDetails || !ccarData) {
      setUserApprover(false);
      return;
    }

    const { approval, overall_approval_status, reject_status } = ccarData;

    const { first_level_approval = [], second_level_approval = [] } =
      approval || {};

    const firstLevelApprover = first_level_approval.find(
      (approver) => approver?.id === userDetails?.id,
    );

    const secondLevelApprover = second_level_approval.find(
      (approver) => approver?.id === userDetails?.id,
    );

    const canVoteFirstLevel =
      firstLevelApprover && !firstLevelApprover?.has_voted;

    const canVoteSecondLevel =
      secondLevelApprover && !secondLevelApprover?.has_voted;

    const requestIsPending =
      !overall_approval_status &&
      !reject_status &&
      ccarData?.final_status === "Pending";

    setUserApprover(
      Boolean(requestIsPending && (canVoteFirstLevel || canVoteSecondLevel)),
    );
  }, [userDetails, ccarData]);

  const getApproverName = (approver) => {
    if (approver?.display_name) {
      return approver.display_name;
    }

    const fullName = [
      approver?.first_name,
      approver?.middle_name,
      approver?.last_name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullName || "Unknown Approver";
  };

  const getApproverStatus = (approver) => {
    if (
      isTruthyValue(approver?.approved) &&
      isTruthyValue(approver?.has_voted)
    ) {
      return {
        label: "Approved",
        className: "request-status-success",
        icon: "fa-check-circle",
      };
    }

    if (
      !isTruthyValue(approver?.approved) &&
      isTruthyValue(approver?.has_voted)
    ) {
      return {
        label: "Rejected",
        className: "request-status-danger",
        icon: "fa-times-circle",
      };
    }

    return {
      label: "Pending",
      className: "request-status-warning",
      icon: "fa-clock-o",
    };
  };

  const isTruthyValue = (value) => {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value).toLowerCase() === "true"
    );
  };

  const getApprovedApprovers = () => {
    const firstLevelApprovers = ccarData?.approval?.first_level_approval || [];

    const secondLevelApprovers =
      ccarData?.approval?.second_level_approval || [];

    const approvedApprovers = [
      ...firstLevelApprovers,
      ...secondLevelApprovers,
    ].filter((approver) => {
      return (
        isTruthyValue(approver?.approved) && isTruthyValue(approver?.has_voted)
      );
    });

    return approvedApprovers.filter(
      (approver, index, approvers) =>
        approvers.findIndex((item) => item?.id === approver?.id) === index,
    );
  };

  const getAttachments = () => {
    if (!ccarData?.prospecting_images) {
      return [];
    }

    try {
      let attachments = JSON.parse(ccarData.prospecting_images);

      // Handle double-stringified JSON.
      if (typeof attachments === "string") {
        attachments = JSON.parse(attachments);
      }

      if (!Array.isArray(attachments)) {
        attachments = [attachments];
      }

      return attachments.filter(Boolean);
    } catch (error) {
      return [ccarData.prospecting_images].filter(Boolean);
    }
  };

  const getAttachmentFileName = (attachment) => {
    return (
      attachment?.toString().split("/").pop()?.split("?")?.[0] || "attachment"
    );
  };

  const getAttachmentFileType = (attachmentName = "") => {
    const parts = attachmentName.split(".");
    if (parts.length < 2) return "Unknown";
    return parts[parts.length - 1].toUpperCase();
  };

  const renderAttachment = (attachment, index) => {
    if (!attachment) {
      return null;
    }

    const cleanUrl = attachment.split("?")[0].toLowerCase();

    const isImage = /\.(png|jpe?g|gif|webp|bmp)$/i.test(cleanUrl);

    const isPdf = /\.pdf$/i.test(cleanUrl);

    const fileName = getAttachmentFileName(attachment);
    const fileType = getAttachmentFileType(fileName);
    const isPreviewable = isImage || isPdf;

    return (
      <div className="attachment-library-item" key={`${attachment}-${index}`}>
        <div className="attachment-library-item__main">
          <i
            className={`fa ${
              isPdf
                ? "fa-file-pdf-o"
                : isImage
                ? "fa-file-image-o"
                : "fa-file-o"
            }`}
            aria-hidden="true"
          ></i>
          <div>
            <strong>{fileName}</strong>
            <span>{fileType} | Size: Unknown</span>
            <small>CCAR Attachment</small>
          </div>
        </div>

        <div className="attachment-library-item__actions">
          <button
            type="button"
            className="request-inline-action"
            onClick={() => {
              if (isPreviewable) {
                setPreviewImage(attachment);
                return;
              }

              window.open(attachment, "_blank", "noopener,noreferrer");
            }}
          >
            {isPreviewable ? "View" : "Open"}
          </button>
          <a
            href={attachment}
            target="_blank"
            rel="noopener noreferrer"
            className="request-inline-action"
          >
            Download
          </a>
        </div>
      </div>
    );
  };

  const approvedApprovers = getApprovedApprovers();

  const attachments = getAttachments();

  const isRequestApproved =
    ccarData?.overall_approval_status === true ||
    ccarData?.final_status === "Approved";

  const isRequestRejected =
    ccarData?.reject_status === true || ccarData?.final_status === "Rejected";

  const approval = ccarData?.approval || {};
  const firstLevelApprovers = approval?.first_level_approval || [];
  const secondLevelApprovers = approval?.second_level_approval || [];

  const initialRequiredCount =
    approval?.initial_approval_needed_count ?? firstLevelApprovers.length ?? 0;
  const executiveRequiredCount =
    approval?.executive_approval_needed_count ??
    secondLevelApprovers.length ??
    0;

  const initialApprovedCount = firstLevelApprovers.filter(
    (approver) =>
      isTruthyValue(approver?.approved) && isTruthyValue(approver?.has_voted),
  ).length;

  const executiveApprovedCount = secondLevelApprovers.filter(
    (approver) =>
      isTruthyValue(approver?.approved) && isTruthyValue(approver?.has_voted),
  ).length;

  const pageStatus = useMemo(() => {
    if (isRequestRejected) {
      return {
        label: "Rejected",
        icon: "fa-times-circle",
        className: "request-status-danger",
        helperText: "This CCAR has been rejected.",
      };
    }

    if (isRequestApproved) {
      return {
        label: "Approved",
        icon: "fa-check-circle",
        className: "request-status-success",
        helperText: "All required approvals are complete.",
      };
    }

    if (initialApprovedCount > 0 || executiveApprovedCount > 0) {
      return {
        label: "Partially Approved",
        icon: "fa-hourglass-half",
        className: "request-status-warning",
        helperText: "This CCAR is currently in approval progress.",
      };
    }

    return {
      label: "Pending",
      icon: "fa-clock-o",
      className: "request-status-warning",
      helperText: "No approval action has been completed yet.",
    };
  }, [
    isRequestRejected,
    isRequestApproved,
    initialApprovedCount,
    executiveApprovedCount,
  ]);

  const currentWorkflowStage = useMemo(() => {
    if (isRequestApproved || isRequestRejected) {
      return "Final State";
    }

    if (initialApprovedCount < initialRequiredCount) {
      return "Initial Approval";
    }

    if (executiveApprovedCount < executiveRequiredCount) {
      return "Executive Approval";
    }

    return "Final State";
  }, [
    isRequestApproved,
    isRequestRejected,
    initialApprovedCount,
    initialRequiredCount,
    executiveApprovedCount,
    executiveRequiredCount,
  ]);

  const getWorkflowStageState = (stage) => {
    if (isRequestRejected) {
      if (stage === "Final State") return "blocked";
      return "completed";
    }

    if (stage === "Initial Approval") {
      if (initialApprovedCount >= initialRequiredCount) return "completed";
      return "current";
    }

    if (stage === "Executive Approval") {
      if (initialApprovedCount < initialRequiredCount) return "upcoming";
      if (executiveApprovedCount >= executiveRequiredCount) return "completed";
      return "current";
    }

    if (isRequestApproved) return "completed";
    return "upcoming";
  };

  const workflowSteps = [
    {
      key: "initial",
      name: "Initial Approval",
      detail: `${initialApprovedCount} of ${initialRequiredCount} approved`,
      icon: "fa-user-circle",
      state: getWorkflowStageState("Initial Approval"),
    },
    {
      key: "executive",
      name: "Executive Approval",
      detail: `${executiveApprovedCount} of ${executiveRequiredCount} approved`,
      icon: "fa-briefcase",
      state: getWorkflowStageState("Executive Approval"),
    },
    {
      key: "final",
      name: "Final State",
      detail: pageStatus.label,
      icon: "fa-flag-checkered",
      state: getWorkflowStageState("Final State"),
    },
  ];

  const requestDescription =
    ccarData?.nc_description || "No description provided.";
  const isDescriptionLong = requestDescription.length > 280;

  const leaveRequestDetail = () => {
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.href = CCAR_LIST_URL;
  };

  const handleGoBack = (event) => {
    if (returnUrl) return;

    event.preventDefault();
    leaveRequestDetail();
  };

  const handleCopyReference = () => {
    if (!ccarData?.reference_number) return;

    navigator.clipboard.writeText(ccarData.reference_number);
    setCopiedReference(true);

    setTimeout(() => {
      setCopiedReference(false);
    }, 1800);
  };

  if (loading) {
    return (
      <div className="loader--container">
        <div className="loader--"></div>
      </div>
    );
  }

  if (!ccarData) {
    return (
      <div className="request-detail--card">
        <h2>CCAR Request Not Found</h2>
      </div>
    );
  }

  return (
    <div className="view-request-detail-container request-detail-shell">
      <a href={returnUrl || CCAR_LIST_URL} onClick={handleGoBack}>
        <div className="go-back">
          <i className="fa fa-arrow-left fa-3x" aria-hidden="true"></i>

          <span>Go Back</span>
        </div>
      </a>

      <section className="request-detail-hero" aria-labelledby="ccar-title">
        <div>
          <span className="request-detail-eyebrow">Request Detail</span>
          <h1 id="ccar-title">Customer Corrective Action Request</h1>
        </div>

        <div className="request-detail-hero__meta">
          <span className={`request-status-badge ${pageStatus.className}`}>
            <i className={`fa ${pageStatus.icon}`} aria-hidden="true"></i>
            <span>{pageStatus.label}</span>
          </span>

          <span className="request-meta-pill">
            <i className="fa fa-archive" aria-hidden="true"></i>
            <span>NC Source: {ccarData?.nc_source || "N/A"}</span>
          </span>

          <span className="request-meta-pill">
            <i className="fa fa-clock-o" aria-hidden="true"></i>
            <span>
              {ccarData?.createdAt
                ? convertTo12HourPlusDateFormat(ccarData?.createdAt)
                : "No submission date"}
            </span>
          </span>
        </div>
      </section>

      <section className="request-summary-grid" aria-label="CCAR Summary">
        <div className="request-summary-block">
          <h3>Request Information</h3>
          <dl>
            <div>
              <dt>Reference Number</dt>
              <dd>{ccarData?.reference_number || "N/A"}</dd>
            </div>

            <div>
              <dt>Issued To</dt>
              <dd>{ccarData?.issued_to || "N/A"}</dd>
            </div>

            <div>
              <dt>Warehouse</dt>
              <dd>{ccarData?.warehouse || "N/A"}</dd>
            </div>

            <div>
              <dt>Contact Person</dt>
              <dd>{ccarData?.contact_person || "N/A"}</dd>
            </div>

            <div>
              <dt>Contact Number</dt>
              <dd>{ccarData?.contact_number || "N/A"}</dd>
            </div>

            <div>
              <dt>Email Address</dt>
              <dd>{ccarData?.email_address || "N/A"}</dd>
            </div>

            <div>
              <dt>Date Submitted</dt>
              <dd>
                {ccarData?.createdAt
                  ? convertTo12HourPlusDateFormat(ccarData.createdAt)
                  : "N/A"}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section
        className="request-description"
        aria-labelledby="ccar-details-title"
      >
        <div className="request-section-header">
          <h2 id="ccar-details-title">Corrective Action Details</h2>
          {isDescriptionLong && (
            <button
              type="button"
              className="request-inline-action"
              onClick={() => setShowFullDescription((prevState) => !prevState)}
            >
              {showFullDescription ? "Show less" : "Show more"}
            </button>
          )}
        </div>

        <pre className="request-description__text">
          {isDescriptionLong && !showFullDescription
            ? `${requestDescription.slice(0, 280)}...`
            : requestDescription}
        </pre>
      </section>

      <section
        className="request-attachments-section"
        aria-labelledby="ccar-attachments-title"
      >
        <div className="request-section-header">
          <h2 id="ccar-attachments-title">Attachments</h2>
          <span className="request-items-count">
            {attachments.length} files
          </span>
        </div>

        {attachments.length > 0 ? (
          <div className="attachment-library-list">
            {attachments.map((attachment, index) =>
              renderAttachment(attachment, index),
            )}
          </div>
        ) : (
          <div className="request-items-empty">
            No attachments available for this CCAR request.
          </div>
        )}
      </section>

      <section
        className="ccar-approval-details-section"
        aria-labelledby="ccar-approval-details-title"
      >
        <div className="request-section-header">
          <h2 id="ccar-approval-details-title">Approval Details</h2>

          <span className="request-items-count">
            {approvedApprovers.length} approved
          </span>
        </div>

        {approvedApprovers.length > 0 ? (
          <div className="ccar-approved-by-list">
            {approvedApprovers.map((approver) => (
              <div className="ccar-approved-by-item" key={approver.id}>
                <div className="ccar-approved-by-avatar">
                  {getApproverName(approver).charAt(0)}
                </div>

                <div className="ccar-approved-by-information">
                  <strong>{getApproverName(approver)}</strong>

                  <span>
                    Approved on{" "}
                    {approver.date
                      ? convertTo12HourPlusDateFormat(approver.date)
                      : "N/A"}
                  </span>

                  {/* Remarks */}
                  {approver.remarks && (
                    <div className="ccar-approval-remarks">
                      <strong>Remarks</strong>
                      <p>{approver.remarks}</p>
                    </div>
                  )}

                  {/* Supporting document */}
                  {approver.attachment && (
                    <div className="ccar-approval-attachment">
                      <strong>Supporting Document</strong>

                      <div className="ccar-approval-attachment-actions">
                        <button
                          type="button"
                          className="attachment-btn"
                          onClick={() => {
                            setPreviewImage(approver.attachment);
                          }}
                        >
                          <i className="fa fa-eye" aria-hidden="true" />
                          Preview
                        </button>

                        <a
                          href={approver.attachment}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-btn"
                        >
                          <i
                            className="fa fa-external-link"
                            aria-hidden="true"
                          />
                          Open
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ccar-approved-by-empty">
            No approvals have been completed yet.
          </div>
        )}
      </section>

      {isUserApprover &&
        !ccarData?.overall_approval_status &&
        !ccarData?.reject_status &&
        ccarData?.final_status === "Pending" && (
          <section
            className="approver--actions request-actions-footer"
            aria-label="CCAR Actions"
          >
            <button
              type="button"
              className="action-btn approve-btn"
              onClick={() => handleApproveDenyRequest(true)}
            >
              Approve Request
            </button>

            <button
              type="button"
              className="action-btn deny-btn"
              onClick={() => handleApproveDenyRequest(false)}
            >
              Deny Request
            </button>
          </section>
        )}

      {showApprovalModal && (
        <div className="ccar-approval-modal-overlay">
          <div className="ccar-approval-modal">
            <div className="ccar-approval-modal-header">
              <div>
                <h3>{approvalStatus ? "Approve Request" : "Deny Request"}</h3>

                <p>
                  Please provide your remarks and upload any supporting document
                  before proceeding.
                </p>
              </div>

              <button
                type="button"
                className="ccar-approval-modal-close"
                onClick={() => setShowApprovalModal(false)}
              >
                &times;
              </button>
            </div>

            <div className="ccar-approval-modal-body">
              {/* Remarks */}
              <div className="ccar-approval-form-group">
                <label htmlFor="approval-remarks">Remarks</label>

                <textarea
                  id="approval-remarks"
                  value={approvalRemarks}
                  placeholder="Enter your remarks..."
                  rows={5}
                  onChange={(event) => setApprovalRemarks(event.target.value)}
                />
              </div>

              {/* Upload Document */}
              <div className="ccar-approval-form-group">
                <label htmlFor="approval-document">Supporting Document</label>

                <div
                  className="ccar-document-upload"
                  onClick={() => approvalFileInputRef.current?.click()}
                >
                  <input
                    ref={approvalFileInputRef}
                    id="approval-document"
                    type="file"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setApprovalAttachment(file);
                    }}
                  />

                  <div className="ccar-upload-placeholder">
                    <i className="fa fa-cloud-upload" aria-hidden="true"></i>

                    <span>
                      {approvalAttachment
                        ? approvalAttachment.name
                        : "Choose a document to upload"}
                    </span>
                  </div>
                </div>

                {approvalAttachment && (
                  <div className="ccar-selected-document">
                    <div>
                      <i className="fa fa-file-o" aria-hidden="true"></i>

                      <span>{approvalAttachment.name}</span>
                    </div>

                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();

                        setApprovalAttachment(null);

                        if (approvalFileInputRef.current) {
                          approvalFileInputRef.current.value = "";
                        }
                      }}
                    >
                      <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="ccar-approval-modal-footer">
              <button
                type="button"
                className="ccar-modal-cancel-btn"
                onClick={() => {
                  setShowApprovalModal(false);
                  setApprovalRemarks("");
                  setApprovalAttachment(null);
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className={
                  approvalStatus
                    ? "ccar-modal-confirm-btn approve"
                    : "ccar-modal-confirm-btn deny"
                }
                disabled={isApprovalSubmitting}
                onClick={submitApproval}
              >
                {isApprovalSubmitting
                  ? "Submitting..."
                  : approvalStatus
                  ? "Approve Request"
                  : "Deny Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ImagePreviewModal
        image={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
}

export default CCARRequestDetail;
